import React from 'react';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';
import { D3TooltipBuff, D3TooltipItem } from 'tooltips';
import { ItemIcon, SkillHeader, SkillActive, SkillInfo, SkillParams, SkillBoxes } from './skillInfo';
import { getIconPath } from 'common';
import { getStats } from 'logic/stats';
import { cubeGetStat, cubeSlotValid } from 'logic/cube';
import { affixShown } from 'logic/skills';

const _L = Locale("ui-skills.js");

function AffixTooltip({ affix, slot, children, follower, soulShard }) {
  if (slot) {
    return <D3TooltipItem slot={slot} follower={follower} soulShard={soulShard}>{children}</D3TooltipItem>;
  } else if (Data.itemaffixes[affix].shrineBuff) {
    return <D3TooltipBuff id={Data.itemaffixes[affix].shrineBuff}>{children}</D3TooltipBuff>;
  } else {
    return children;
  }
}

export const AffixCard = React.memo(function AffixCard({ affix, slot, item, set, pieces, follower, itemFollower }) {
  const info = Data.itemaffixes[affix];
  if (!info) return null;

  let icon = item && <ItemIcon id={item.id}/>;
  let name;
  if (set) {
    name = Data.itemSets[set].name;
    if (pieces) {
      name = <span>{name} <span className="d3p-gem-rank">&#x2013; {_L`${pieces} pieces`}</span></span>;
    }
  } else if (item) {
    name = item.name;
  } else if (info.shrineBuff && Data.shrineBuffs[info.shrineBuff]) {
    const buff = Data.shrineBuffs[info.shrineBuff];
    name = buff.name;
    icon = <span className="d3p-item-icon" style={{backgroundImage: `url(${getIconPath(buff.icon)})`}}/>;
  } else {
    return null;
  }

  return (
    <div className="d3p-SkillCard d3p-affix">
      <AffixTooltip affix={affix} slot={item && slot} follower={itemFollower} soulShard={info.soulshard}>
        <SkillHeader icon={icon} name={name}>
          <div className="d3p-passive-extra">
            {!!info.setActive && <SkillActive info={info}/>}
            {!!info.boxnames && <SkillBoxes info={info}/>}
            {!!info.params && <SkillParams info={info}/>}
            {!!info.info && <SkillInfo type="affix" id={affix} rune={follower}/>}
          </div>
        </SkillHeader>
      </AffixTooltip>
    </div>
  );
});

export default function AffixSection({ follower }) {
  const state = Store.useState();
  let stats = getStats(state);
  if (follower) stats = stats.follower;
  if (!stats) return null;

  const profile = state.profiles[state.activeProfile];

  const kanaiFx = [];
  if (!follower) {
    for (const slot in profile.kanai) {
      if (cubeSlotValid(profile, slot)) {
        const stat = cubeGetStat(profile.kanai[slot]);
        if (stat) kanaiFx.push(stat[0]);
      }
    }
  }

  const affixes = Object.keys(stats.affixes).filter(affix => {
    if (kanaiFx.includes(affix)) return false;
    return affixShown(affix, stats);
  });

  if (!affixes.length) return null;

  return (
    <React.Fragment>
      <h3 className="d3p-skill-category">{_L`Item Effects`}</h3>
      {affixes.map(affix => {
        let { slot, set, pieces, follower: itemFollower } = stats.affixes[affix];
        const itemData = slot && ((follower || itemFollower) ? profile.followerItems[slot] : profile.items[slot]);
        let item = itemData && Data.itemById[itemData.id];
        if (Data.itemaffixes[affix]?.soulshard && itemData?.gems?.[0]?.id) {
          item = Data.itemById[itemData.gems[0].id];
        }
        return <AffixCard key={affix} affix={affix} slot={slot} item={item} set={set} pieces={pieces} follower={follower} itemFollower={itemFollower || follower}/>;
      })}
    </React.Fragment>
  );
}
